import { AuthenticationResult, EventType } from '@azure/msal-browser';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getMsalInstance } from 'authConfig';
import { MaintMessage, maintWindow } from 'maintenance';
import 'react-dates/initialize';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { ReactNotifications } from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';
import { Provider } from 'react-redux';
import App from './app/App';
import { queryClient } from './app/queryClient';
import store from './app/store';
import { registerAnalyticsStateGetter } from './helpers/analytics';
import './i18n';
import './index.css';
import './materialUI/scss/material-dashboard-pro-react.scss?v=1.8.0';
import reportWebVitals from './reportWebVitals';

const emotionInsertionPoint = document.querySelector<HTMLMetaElement>(
  'meta[name="emotion-insertion-point"]',
)

const emotionCache = createCache({
  key: 'css',
  insertionPoint: emotionInsertionPoint ?? undefined,
})

async function bootstrap() {
  try {
    const msalInstance = await getMsalInstance()

    let redirectResult: AuthenticationResult | null = null
    try {
      redirectResult = (await msalInstance.handleRedirectPromise()) ?? null
    } catch (err) {
      console.error('[Bootstrap] MSAL handleRedirectPromise error:', err)
    }

    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account)
    }

    msalInstance.addEventCallback((event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        event?.payload
      ) {
        const payload = event.payload as AuthenticationResult
        msalInstance.setActiveAccount(payload.account)
      }
    })

    if (!msalInstance.getActiveAccount()) {
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0])
      }
    }

    registerAnalyticsStateGetter(() => store.getState())

    if (process.env.NODE_ENV === 'production') {
      console.log = () => {}
      console.error = () => {}
      console.debug = () => {}
    }

    const container = document.getElementById('root')
    if (!container) {
      throw new Error('Root container #root not found')
    }
    const root = createRoot(container)
    root.render(
      <CacheProvider value={emotionCache}>
        <HelmetProvider>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <ReactNotifications />
              {maintWindow() ? (
                <MaintMessage />
              ) : (
                <App instance={msalInstance} />
              )}
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </Provider>
        </HelmetProvider>
      </CacheProvider>,
    )
  } catch (e) {
    console.log(e)
  }
}

bootstrap()

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/here-tile-sw.js', { scope: '/' })
      .catch((err) =>
        console.error('[SW] HERE tile cache registration failed', err),
      )
  })
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
